import { useEffect, useState } from 'react'
import { Table, Tag, Button, message, Modal, Descriptions, Card, Row, Col, Input, Select, Space, Form, InputNumber } from 'antd'
import { EyeOutlined, SearchOutlined, ReloadOutlined, PlusOutlined, MinusCircleOutlined, PrinterOutlined } from '@ant-design/icons'
import { orderApi } from '../services/api'
import dayjs from 'dayjs'
import { OrderPrint } from '../components/PrintLabel'

interface OrderItem {
  id: number
  product_id: number
  product_name: string
  product_code: string
  quantity: number
  unit: string
  unit_price: number
  total_price: number
  product?: {
    main_image?: string
  }
}

interface Order {
  id: number
  order_no: string
  user_id: number
  user_type: number
  delivery_date: string
  total_amount: number
  discount_amount: number
  final_amount: number
  remark?: string
  status: number
  created_at: string
  paid_at?: string
  user?: {
    id: number
    phone: string
  }
  items?: OrderItem[]
  deliveryAddress?: {
    contact_name: string
    contact_phone: string
    address: string
  }
}

const statusMap: Record<number, { color: string; text: string }> = {
  0: { color: 'warning', text: '待支付' },
  1: { color: 'processing', text: '已支付' },
  2: { color: 'blue', text: '处理中' },
  3: { color: 'cyan', text: '配送中' },
  4: { color: 'success', text: '已完成' },
  5: { color: 'default', text: '已取消' }
}

const userTypeMap: Record<number, string> = {
  1: '个人客户',
  2: '企业客户'
}

function Orders() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [filters, setFilters] = useState({ keyword: '', status: undefined as number | undefined })

  // 详情弹窗
  const [detailOpen, setDetailOpen] = useState(false)
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  
  // 打印弹窗
  const [printOpen, setPrintOpen] = useState(false)

  // 新增订单弹窗
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm] = Form.useForm()

  const fetchOrders = async (page = 1, pageSize = 10, extraFilters?: any) => {
    setLoading(true)
    const f = extraFilters || filters
    try {
      const res: any = await orderApi.getOrders({
        page,
        page_size: pageSize,
        keyword: f.keyword || undefined,
        status: f.status
      })
      if (res.code === 200) {
        setOrders(res.data.list || [])
        setPagination({ current: page, pageSize, total: res.data.total })
      }
    } catch {
      message.error('获取订单列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchOrders() }, [])

  const handleSearch = () => fetchOrders(1, pagination.pageSize)
  const handleReset = () => {
    const f = { keyword: '', status: undefined }
    setFilters(f)
    fetchOrders(1, pagination.pageSize, f)
  }

  const handleViewDetail = async (order: Order) => {
    setDetailOpen(true)
    setDetailLoading(true)
    try {
      const res: any = await orderApi.getOrder(order.id)
      if (res.code === 200) {
        setCurrentOrder(res.data)
      } else {
        message.error('获取订单详情失败')
      }
    } catch {
      message.error('获取订单详情失败')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleUpdateStatus = async (orderId: number, status: number) => {
    try {
      const res: any = await orderApi.updateStatus(orderId, { status })
      if (res.code === 200) {
        message.success('状态更新成功')
        fetchOrders(pagination.current, pagination.pageSize)
        if (currentOrder?.id === orderId) {
          setCurrentOrder({ ...currentOrder, status })
        }
      } else if (res.message) {
        // 库存不足等错误，简单提示
        message.error('库存不足')
      }
    } catch {
      message.error('状态更新失败')
    }
  }

  // 创建测试订单
  const handleCreateOrder = async () => {
    try {
      const values = await createForm.validateFields()
      const items = values.items.map((it: any) => ({
        product_id: it.product_id,
        quantity: it.quantity
      }))
      const res: any = await orderApi.createOrder({
        delivery_date: values.delivery_date,
        items
      })
      if (res.code === 200) {
        message.success('订单创建成功')
        setCreateOpen(false)
        createForm.resetFields()
        fetchOrders(1, pagination.pageSize)
      } else {
        message.error(res.message || '创建失败')
      }
    } catch (e: any) {
      if (!e?.errorFields) message.error('创建失败')
    }
  }

  const columns = [
    { title: '订单编号', dataIndex: 'order_no', key: 'order_no', width: 180 },
    { title: '客户', key: 'customer', render: (_: any, r: Order) => r.user?.phone || '-' },
    { title: '客户类型', dataIndex: 'user_type', key: 'user_type', width: 100, render: (v: number) => userTypeMap[v] || '-' },
    { title: '金额', dataIndex: 'final_amount', key: 'final_amount', render: (v: number) => `¥${Number(v).toFixed(2)}` },
    { title: '配送日期', dataIndex: 'delivery_date', key: 'delivery_date', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: number) => {
        const s = statusMap[v] || { color: 'default', text: '未知' }
        return <Tag color={s.color}>{s.text}</Tag>
      }
    },
    { title: '下单时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: (v: string) => v ? dayjs(v).format('MM-DD HH:mm') : '-' },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, r: Order) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(r)}>详情</Button>
      )
    }
  ]

  return (
    <div>
      {/* 搜索栏 */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <Row gutter={12} align="middle">
          <Col>
            <Input
              placeholder="搜索订单号/手机号"
              prefix={<SearchOutlined />}
              value={filters.keyword}
              onChange={e => setFilters(f => ({ ...f, keyword: e.target.value }))}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="订单状态"
              value={filters.status}
              onChange={v => setFilters(f => ({ ...f, status: v }))}
              style={{ width: 120 }}
              allowClear
            >
              {Object.entries(statusMap).map(([k, v]) => (
                <Select.Option key={k} value={Number(k)}>{v.text}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} style={{ backgroundColor: '#55266c' }}>搜索</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <h2 style={{ marginBottom: 16 }}>
        <span>订单管理</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)} style={{ float: 'right', backgroundColor: '#55266c' }}>
          新增订单
        </Button>
      </h2>
      <Table
        columns={columns}
        dataSource={orders}
        rowKey="id"
        loading={loading}
        pagination={{ ...pagination, showSizeChanger: true, showTotal: (t) => `共 ${t} 个订单` }}
        onChange={(p) => fetchOrders(p.current!, p.pageSize!)}
        size="middle"
      />

      {/* 订单详情弹窗 */}
      <Modal
        title={`订单详情 - ${currentOrder?.order_no || ''}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>关闭</Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)}>
            打印配货单
          </Button>
        ]}
        width={700}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
        ) : currentOrder ? (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="订单编号">{currentOrder.order_no}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusMap[currentOrder.status]?.color}>{statusMap[currentOrder.status]?.text}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="客户">{currentOrder.user?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="客户类型">{userTypeMap[currentOrder.user_type] || '-'}</Descriptions.Item>
              <Descriptions.Item label="配送日期">{currentOrder.delivery_date || '-'}</Descriptions.Item>
              <Descriptions.Item label="下单时间">{currentOrder.created_at ? dayjs(currentOrder.created_at).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
              {currentOrder.deliveryAddress && (
                <>
                  <Descriptions.Item label="收货人">{currentOrder.deliveryAddress.contact_name}</Descriptions.Item>
                  <Descriptions.Item label="联系电话">{currentOrder.deliveryAddress.contact_phone}</Descriptions.Item>
                  <Descriptions.Item label="配送地址" span={2}>{currentOrder.deliveryAddress.address}</Descriptions.Item>
                </>
              )}
              {currentOrder.remark && (
                <Descriptions.Item label="备注" span={2}>{currentOrder.remark}</Descriptions.Item>
              )}
            </Descriptions>

            <Card title="商品明细" size="small" style={{ marginBottom: 16 }}>
              <Table
                dataSource={currentOrder.items || []}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: '商品编码', dataIndex: 'product_code', width: 100 },
                  { title: '商品名称', dataIndex: 'product_name' },
                  { title: '单价', dataIndex: 'unit_price', width: 90, render: (v: number) => `¥${Number(v).toFixed(2)}` },
                  { title: '数量', dataIndex: 'quantity', width: 80, render: (v: number, r: OrderItem) => r.unit === '克' ? `${v}/500克` : `${v}${r.unit}` },
                  { title: '小计', dataIndex: 'total_price', width: 90, render: (v: number) => `¥${Number(v).toFixed(2)}` }
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}><strong>合计</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} colSpan={2} />
                    <Table.Summary.Cell index={4}>
                      <strong style={{ color: '#ef4444' }}>¥{Number(currentOrder.final_amount).toFixed(2)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Card>

            {/* 状态操作按钮 */}
            <div style={{ textAlign: 'right' }}>
              {currentOrder.status === 0 && (
                <Button type="primary" style={{ backgroundColor: '#10b981', marginRight: 8 }} onClick={() => handleUpdateStatus(currentOrder.id, 1)}>
                  确认支付
                </Button>
              )}
              {currentOrder.status === 1 && (
                <Button type="primary" style={{ backgroundColor: '#1890ff', marginRight: 8 }} onClick={() => handleUpdateStatus(currentOrder.id, 2)}>
                  开始处理
                </Button>
              )}
              {currentOrder.status === 2 && (
                <Button type="primary" style={{ backgroundColor: '#0891b2', marginRight: 8 }} onClick={() => handleUpdateStatus(currentOrder.id, 3)}>
                  开始配送
                </Button>
              )}
              {currentOrder.status === 3 && (
                <Button type="primary" style={{ backgroundColor: '#10b981', marginRight: 8 }} onClick={() => handleUpdateStatus(currentOrder.id, 4)}>
                  确认送达
                </Button>
              )}
              <Button danger onClick={async () => {
                try {
                  await new Promise((resolve, reject) => {
                    Modal.confirm({
                      title: '确认删除订单',
                      content: '删除后所有信息将无法恢复，是否确认删除？',
                      okText: '确认删除',
                      okButtonProps: { danger: true },
                      cancelText: '取消',
                      onOk: resolve,
                      onCancel: reject
                    })
                  })
                  const res: any = await orderApi.deleteOrder(currentOrder.id)
                  if (res.code === 200) {
                    message.success('订单已删除')
                    setDetailOpen(false)
                    fetchOrders()
                  } else {
                    message.error(res.message || '删除失败')
                  }
                } catch {}
              }} style={{ marginRight: 8 }}>删除订单</Button>
              {currentOrder.status < 4 && (
                <Button danger onClick={() => { handleUpdateStatus(currentOrder.id, 5); setDetailOpen(false); }}>取消订单</Button>
              )}
            </div>
          </>
        ) : null}
      </Modal>

      {/* 新增订单弹窗 */}
      <Modal
        title="新增测试订单"
        open={createOpen}
        onOk={handleCreateOrder}
        onCancel={() => setCreateOpen(false)}
        okText="创建订单"
        okButtonProps={{ style: { backgroundColor: '#55266c' } }}
        width={600}
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="delivery_date" label="配送日期" rules={[{ required: true, message: '请选择配送日期' }]}>
            <Input type="date" />
          </Form.Item>
          <Form.List name="items" initialValue={[{ product_id: 1, quantity: 1 }]}>
            {(fields, { add, remove }) => (
              <>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>商品明细</div>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                    <Col flex="auto">
                      <Form.Item {...restField} name={[name, 'product_id']} rules={[{ required: true, message: '商品ID' }]} noStyle>
                        <InputNumber placeholder="商品ID" min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'quantity']} rules={[{ required: true, message: '数量' }]} noStyle>
                        <InputNumber placeholder="数量" min={0.5} step={0.5} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      {fields.length > 1 && (
                        <MinusCircleOutlined style={{ color: '#ef4444', cursor: 'pointer', lineHeight: '32px' }} onClick={() => remove(name)} />
                      )}
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>添加商品</Button>
              </>
            )}
          </Form.List>
          <div style={{ marginTop: 12, color: '#666', fontSize: 12 }}>
            提示：商品ID可从商品管理页面查看，如：1=青菜, 2=油麦菜...
          </div>
        </Form>
      </Modal>

      {/* 打印配货单弹窗 */}
      <Modal
        title="打印配货单"
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPrintOpen(false)}>关闭</Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
            打印
          </Button>
        ]}
        width={800}
      >
        {currentOrder && (
          <div className="print-area">
            <OrderPrint order={currentOrder} title="配货单" />
          </div>
        )}
      </Modal>
    </div>
  )
}

// 打印样式
const printStyles = `
@media print {
  body * { visibility: hidden; }
  .print-area, .print-area * { visibility: visible; }
  .print-area { position: absolute; left: 0; top: 0; width: 100%; }
  .ant-modal-wrap, .ant-modal-mask { display: none !important; }
}
`
if (typeof document !== 'undefined') {
  const style = document.createElement('style')
  style.textContent = printStyles
  document.head.appendChild(style)
}

export default Orders
